import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { hoyEcuador } from '../../utils/fechas';
import PagoMixto, { lineasMixtoIniciales, resumirPagoMixto } from '../../comun/PagoMixto';
import SelectorVendedor, { leerUltimoVendedor } from '../../comun/SelectorVendedor';

const Escuelas = () => {
    const navigate = useNavigate();

    // Estados
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'

    // Modal de Creación / Edición
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentSchoolId, setCurrentSchoolId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        contact_person: '',
        phone: '',
        address: '',
        default_delivery_time: '09:30',
        is_active: true,
        notes: ''
    });

    // Modal de Despacho Rápido desde Escuelas
    const [showDispatchModal, setShowDispatchModal] = useState(false);
    const [selectedSchoolForDispatch, setSelectedSchoolForDispatch] = useState(null);
    const [dispatchDate, setDispatchDate] = useState(() => hoyEcuador());
    const [dispatchQuantity, setDispatchQuantity] = useState(25);
    const [dispatchPaymentMethod, setDispatchPaymentMethod] = useState('efectivo');
    const [dispatchTransferRef, setDispatchTransferRef] = useState('');
    const [dispatchNotes, setDispatchNotes] = useState('');
    const [dispatchSubmitting, setDispatchSubmitting] = useState(false);

    // Pago Mixto: varias formas de pago en la misma entrega (ej: $30 efectivo + $10 transferencia + $20 fiado)
    const [mixLines, setMixLines] = useState(lineasMixtoIniciales());
    const [exchangeRate] = useState(parseFloat(localStorage.getItem('usdExchangeRate')) || 4000);

    // Trabajador que realiza la venta
    const [sellerId, setSellerId] = useState(leerUltimoVendedor());

    const dispatchTotal = useMemo(() => {
        return parseFloat(dispatchQuantity || 0) * 1.00;
    }, [dispatchQuantity]);

    // Resumen del pago mixto (efectivo, transferencia, tarjeta y/o fiado)
    const mixSummary = useMemo(
        () => resumirPagoMixto(mixLines, dispatchTotal, exchangeRate),
        [mixLines, dispatchTotal, exchangeRate]
    );

    // Selector de Cliente en Despacho Rápido
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        email: '',
        password: 'Password123!',
        password_confirmation: 'Password123!',
        first_name: '',
        last_name: '',
        phone: '',
        address: '',
        city: '',
        cedula: ''
    });

    // Validación del pago mixto: debe cubrir el total y, si hay fiado, tener cliente
    const isDispatchPaymentValid = useMemo(() => {
        if (dispatchPaymentMethod !== 'dividido') return true;
        return mixSummary.valid;
    }, [dispatchPaymentMethod, mixSummary]);

    const searchCustomers = async (query) => {
        setCustomerSearch(query);
        if (query.length < 3) {
            setCustomers([]);
            return;
        }
        try {
            const res = await api.post('/api/customers/admin/search/', { query }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setCustomers(res.data?.data?.customers || []);
        } catch (err) {
            console.error('Error buscando clientes:', err);
        }
    };

    const handleCreateCustomer = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post('/api/customers/register/', {
                ...newCustomer,
                cedula: newCustomer.cedula || null
            }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            const c = res.data?.data?.customer;
            alert('Cliente creado exitosamente');
            setShowCustomerModal(false);
            setSelectedCustomer(c);
            setCustomerSearch(`${c.first_name} ${c.last_name}`);
            setCustomers([]);
            setNewCustomer({
                email: '',
                password: 'Password123!',
                password_confirmation: 'Password123!',
                first_name: '',
                last_name: '',
                phone: '',
                address: '',
                city: '',
                cedula: ''
            });
        } catch (err) {
            const errorData = err.response?.data;
            let msg = 'Error al crear cliente';
            if (errorData?.errors) {
                msg += ':\n' + Object.entries(errorData.errors).map(([k, v]) => `- ${k}: ${v}`).join('\n');
            } else if (errorData?.message) {
                msg += ': ' + errorData.message;
            } else {
                msg += ': ' + err.message;
            }
            alert(msg);
        }
    };

    const handleOpenDispatchModal = (school) => {
        setSelectedSchoolForDispatch(school);
        setSelectedCustomer(null);
        setCustomerSearch('');
        setCustomers([]);
        setDispatchDate(hoyEcuador());
        setDispatchQuantity(25);
        setDispatchPaymentMethod('efectivo');
        setDispatchTransferRef('');
        setDispatchNotes('');
        setMixLines(lineasMixtoIniciales());
        setShowDispatchModal(true);
    };

    const handleQuickDispatchSubmit = async (e) => {
        e.preventDefault();
        if (!selectedSchoolForDispatch) return;
        const qty = parseInt(dispatchQuantity, 10);
        if (isNaN(qty) || qty <= 0) {
            alert('Por favor ingresa una cantidad válida de loncheras.');
            return;
        }

        const totalNum = parseFloat(dispatchTotal.toFixed(2));
        const totalAmount = dispatchTotal.toFixed(2);
        let cashAmount = 0;
        let transferAmount = 0;
        let cardAmount = 0;
        let creditAmount = 0;
        let splitNote = '';

        if (dispatchPaymentMethod === 'dividido') {
            if (!mixSummary.valid) {
                alert(`Los montos del pago mixto ($${mixSummary.entered.toFixed(2)} USD) no cubren el Total a Cobrar ($${totalAmount} USD). Por favor, verifica los montos.`);
                return;
            }
            cashAmount = mixSummary.cash_amount;
            transferAmount = mixSummary.transfer_amount;
            cardAmount = mixSummary.card_amount;
            creditAmount = mixSummary.credit_amount;
            splitNote = mixSummary.note;
        } else if (dispatchPaymentMethod === 'fiado') {
            cashAmount = 0;
            transferAmount = 0;
        } else {
            cashAmount = dispatchPaymentMethod === 'efectivo' ? totalNum : 0;
            transferAmount = dispatchPaymentMethod === 'transferencia' ? totalNum : 0;
        }

        setDispatchSubmitting(true);
        try {
            const menuRes = await api.get('/api/menu/products/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                params: { search: 'Lonchera Escolar' }
            });
            const products = Array.isArray(menuRes.data) ? menuRes.data : (menuRes.data.results || []);
            const lunchboxProduct = products[0];

            if (!lunchboxProduct) {
                throw new Error('No se encontró el producto Lonchera Escolar ($1.00) en el menú.');
            }

            const schoolName = selectedSchoolForDispatch.name;
            const payload = {
                business_type: 'lonchera',
                order_date: dispatchDate,
                school_id: selectedSchoolForDispatch.id,
                school_name: schoolName,
                lunchbox_count: qty,
                order_type: 'delivery',
                payment_method: dispatchPaymentMethod,
                payment_reference: dispatchPaymentMethod === 'transferencia' ? dispatchTransferRef : '',
                cash_amount: cashAmount,
                transfer_amount: transferAmount,
                card_amount: cardAmount,
                credit_amount: creditAmount,
                customer_id: selectedCustomer ? selectedCustomer.id : null,
                seller_id: sellerId || null,
                delivery_info: {
                    address: selectedSchoolForDispatch.address || 'Tulcán',
                    contact_name: selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : (selectedSchoolForDispatch.contact_person || schoolName),
                    contact_phone: (selectedCustomer && selectedCustomer.phone) || selectedSchoolForDispatch.phone || '0999999999',
                    city: 'Tulcán'
                },
                items: [
                    {
                        product_id: lunchboxProduct.id,
                        quantity: qty,
                        notes: `Despacho escolar a ${schoolName} (${qty} cajitas felices)`
                    }
                ],
                notes: [
                    dispatchNotes,
                    `Escuela: ${schoolName}`,
                    selectedCustomer ? `Cliente: ${selectedCustomer.first_name} ${selectedCustomer.last_name}${selectedCustomer.cedula ? ` (CI: ${selectedCustomer.cedula})` : ''}` : '',
                    dispatchPaymentMethod === 'transferencia' && dispatchTransferRef ? `Ref: ${dispatchTransferRef}` : '',
                    splitNote
                ].filter(Boolean).join(' | ')
            };

            await api.post('/api/orders/orders/', payload, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });

            alert(`¡Despacho registrado con éxito!\n\n${qty} loncheras para "${schoolName}"\nTotal: $${totalAmount} USD (${dispatchPaymentMethod === 'dividido' ? 'Pago Mixto' : dispatchPaymentMethod.toUpperCase()})` +
                (creditAmount > 0 ? `\nFiado a ${selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : schoolName}: $${creditAmount.toFixed(2)}` : ''));
            setSelectedCustomer(null);
            setCustomerSearch('');
            setCustomers([]);
            setShowDispatchModal(false);
        } catch (err) {
            console.error('Error registrando despacho:', err);
            const msg = err.response?.data?.detail || err.message;
            alert('Error al registrar despacho: ' + msg);
        } finally {
            setDispatchSubmitting(false);
        }
    };

    useEffect(() => {
        loadSchools();
    }, []);

    const loadSchools = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/orders/schools/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
            setSchools(data);
        } catch (error) {
            console.error('Error cargando escuelas:', error);
            alert('No se pudieron cargar las escuelas. Revisa la conexión con el servidor.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreateModal = () => {
        setIsEditing(false);
        setCurrentSchoolId(null);
        setFormData({
            name: '',
            contact_person: '',
            phone: '',
            address: '',
            default_delivery_time: '09:30',
            is_active: true,
            notes: ''
        });
        setShowModal(true);
    };

    const handleOpenEditModal = (school) => {
        setIsEditing(true);
        setCurrentSchoolId(school.id);
        setFormData({
            name: school.name || '',
            contact_person: school.contact_person || '',
            phone: school.phone || '',
            address: school.address || '',
            default_delivery_time: school.default_delivery_time || '09:30',
            is_active: school.is_active !== false,
            notes: school.notes || ''
        });
        setShowModal(true);
    };

    const handleSaveSchool = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            alert('El nombre de la institución educativa es obligatorio.');
            return;
        }

        setSubmitting(true);
        try {
            if (isEditing && currentSchoolId) {
                await api.put(`/api/orders/schools/${currentSchoolId}/`, formData, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                alert('Escuela actualizada exitosamente.');
            } else {
                await api.post('/api/orders/schools/', formData, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                alert('Nueva escuela registrada exitosamente.');
            }
            setShowModal(false);
            loadSchools();
        } catch (error) {
            console.error('Error guardando escuela:', error);
            const msg = error.response?.data?.name?.[0] || error.response?.data?.detail || error.message;
            alert('Error al guardar escuela: ' + msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (school) => {
        const newStatus = !school.is_active;
        const confirmMsg = newStatus
            ? `¿Deseas activar la escuela "${school.name}"?`
            : `¿Deseas desactivar la escuela "${school.name}"?`;

        if (!window.confirm(confirmMsg)) return;

        try {
            await api.patch(`/api/orders/schools/${school.id}/`, { is_active: newStatus }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            loadSchools();
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            alert('No se pudo cambiar el estado de la escuela.');
        }
    };

    const handleDeleteSchool = async (school) => {
        if (!window.confirm(`¿Estás seguro de eliminar permanentemente la escuela "${school.name}"?`)) {
            return;
        }

        try {
            await api.delete(`/api/orders/schools/${school.id}/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            alert('Escuela eliminada.');
            loadSchools();
        } catch (error) {
            console.error('Error al eliminar escuela:', error);
            alert('No se pudo eliminar la escuela. Puede tener órdenes asociadas.');
        }
    };

    // Filtrar escuelas
    const filteredSchools = schools.filter(school => {
        const matchesSearch = school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (school.contact_person && school.contact_person.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (school.address && school.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (school.phone && school.phone.includes(searchTerm));

        if (!matchesSearch) return false;

        if (filterActive === 'active') return school.is_active;
        if (filterActive === 'inactive') return !school.is_active;
        return true;
    });

    const activeCount = schools.filter(s => s.is_active).length;
    const inactiveCount = schools.length - activeCount;

    return (
        <div style={{ padding: '1rem', maxWidth: '1400px', margin: '0 auto', color: 'var(--text-color)' }}>
            {/* Header del Módulo */}
            <div className="page-header-banner">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <i className="bi bi-buildings-fill" style={{ fontSize: '1.75rem' }}></i>
                        <h1 style={{ margin: 0, fontSize: '1.65rem', fontWeight: 'bold', letterSpacing: '-0.5px' }}>
                            Instituciones Educativas (Escuelas de Tulcán)
                        </h1>
                    </div>
                    <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem', maxWidth: '650px' }}>
                        Administra el catálogo de escuelas, colegios y puntos de entrega para el despacho diario de loncheras escolares ($1.00 USD).
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => navigate('/fast-food/loncheras')}
                        style={{
                            background: 'rgba(255, 255, 255, 0.15)',
                            color: '#FFFFFF',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            padding: '0.65rem 1.15rem',
                            borderRadius: '10px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="bi bi-box-seam"></i> Despachar Loncheras
                    </button>

                    <button
                        onClick={handleOpenCreateModal}
                        style={{
                            background: 'var(--secondary-color)',
                            color: 'var(--primary-color)',
                            border: 'none',
                            padding: '0.65rem 1.25rem',
                            borderRadius: '10px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="bi bi-plus-circle-fill"></i> + Nueva Escuela
                    </button>
                </div>
            </div>

            {/* Tarjetas de Resumen / KPIs */}
            <div className="kpi-grid-responsive">
                <div style={{
                    background: 'var(--surface-color)',
                    padding: '1.25rem',
                    borderRadius: '14px',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '12px',
                        background: '#FAF6F2',
                        color: 'var(--primary-color)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        border: '1px solid #E6D7CD'
                    }}>
                        <i className="bi bi-building"></i>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase' }}>
                            Total Escuelas
                        </div>
                        <div style={{ fontSize: '1.6rem', fontWeight: '800', color: 'var(--primary-color)' }}>
                            {schools.length}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            En la ciudad de Tulcán
                        </div>
                    </div>
                </div>

                <div style={{
                    background: 'var(--surface-color)',
                    padding: '1.25rem',
                    borderRadius: '14px',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '12px',
                        background: '#e6f4ea',
                        color: '#137333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                    }}>
                        <i className="bi bi-check-circle-fill"></i>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase' }}>
                            Escuelas Activas
                        </div>
                        <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#137333' }}>
                            {activeCount}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Habilitadas para despacho
                        </div>
                    </div>
                </div>

                <div style={{
                    background: 'var(--surface-color)',
                    padding: '1.25rem',
                    borderRadius: '14px',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '12px',
                        background: '#fef3c7',
                        color: '#b45309',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                    }}>
                        <i className="bi bi-clock"></i>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase' }}>
                            Horario de Entrega
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#b45309' }}>
                            09:00 - 10:30
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Recreo matutino escolar
                        </div>
                    </div>
                </div>

                <div style={{
                    background: 'var(--surface-color)',
                    padding: '1.25rem',
                    borderRadius: '14px',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '12px',
                        background: '#e0f2fe',
                        color: '#0284c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                    }}>
                        <i className="bi bi-tag-fill"></i>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase' }}>
                            Tarifa Lonchera
                        </div>
                        <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#0284c7' }}>
                            $1.00 <span style={{ fontSize: '0.9rem' }}>USD</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            4 elementos por cajita
                        </div>
                    </div>
                </div>
            </div>

            {/* Barra de Búsqueda y Filtros */}
            <div style={{
                background: 'var(--surface-color)',
                padding: '1rem 1.5rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                marginBottom: '1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '280px' }}>
                    <i className="bi bi-search" style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}></i>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar por nombre de escuela, contacto, teléfono o dirección..."
                        style={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            fontSize: '0.95rem',
                            background: 'transparent'
                        }}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                        >
                            &times;
                        </button>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Estado:</span>
                    <button
                        onClick={() => setFilterActive('all')}
                        style={{
                            padding: '0.4rem 0.85rem',
                            borderRadius: '8px',
                            border: filterActive === 'all' ? '1px solid var(--primary-color)' : '1px solid var(--border-color)',
                            background: filterActive === 'all' ? 'var(--secondary-color)' : '#fff',
                            color: filterActive === 'all' ? 'var(--primary-color)' : 'var(--text-color)',
                            fontWeight: filterActive === 'all' ? '700' : '500',
                            fontSize: '0.85rem',
                            cursor: 'pointer'
                        }}
                    >
                        Todas ({schools.length})
                    </button>
                    <button
                        onClick={() => setFilterActive('active')}
                        style={{
                            padding: '0.4rem 0.85rem',
                            borderRadius: '8px',
                            border: filterActive === 'active' ? '1px solid #16a34a' : '1px solid var(--border-color)',
                            background: filterActive === 'active' ? '#dcfce7' : '#fff',
                            color: filterActive === 'active' ? '#16a34a' : 'var(--text-color)',
                            fontWeight: filterActive === 'active' ? '700' : '500',
                            fontSize: '0.85rem',
                            cursor: 'pointer'
                        }}
                    >
                        Activas ({activeCount})
                    </button>
                    <button
                        onClick={() => setFilterActive('inactive')}
                        style={{
                            padding: '0.4rem 0.85rem',
                            borderRadius: '8px',
                            border: filterActive === 'inactive' ? '1px solid #dc2626' : '1px solid var(--border-color)',
                            background: filterActive === 'inactive' ? '#fee2e2' : '#fff',
                            color: filterActive === 'inactive' ? '#dc2626' : 'var(--text-color)',
                            fontWeight: filterActive === 'inactive' ? '700' : '500',
                            fontSize: '0.85rem',
                            cursor: 'pointer'
                        }}
                    >
                        Inactivas ({inactiveCount})
                    </button>
                </div>
            </div>

            {/* Listado de Escuelas */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-secondary)' }}>
                    Cargando listado de escuelas...
                </div>
            ) : filteredSchools.length === 0 ? (
                <div style={{
                    background: 'var(--surface-color)',
                    borderRadius: '16px',
                    padding: '3rem',
                    textAlign: 'center',
                    border: '1px dashed var(--border-color)'
                }}>
                    <i className="bi bi-buildings" style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem', color: 'var(--primary-color)' }}></i>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary-color)', marginBottom: '0.5rem' }}>
                        No se encontraron escuelas
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                        {searchTerm ? 'No hay resultados que coincidan con la búsqueda.' : 'Aún no tienes escuelas registradas.'}
                    </p>
                    <button
                        onClick={handleOpenCreateModal}
                        style={{
                            background: 'var(--primary-color)',
                            color: '#FFFFFF',
                            border: 'none',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '10px',
                            fontWeight: '700',
                            cursor: 'pointer'
                        }}
                    >
                        + Registrar la Primera Escuela
                    </button>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1.25rem'
                }}>
                    {filteredSchools.map((school) => (
                        <div
                            key={school.id}
                            style={{
                                background: 'var(--surface-color)',
                                borderRadius: '14px',
                                border: '1px solid var(--border-color)',
                                padding: '1.5rem',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                opacity: school.is_active ? 1 : 0.65,
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                        <i className="bi bi-building-fill" style={{ fontSize: '1.25rem', color: 'var(--primary-color)' }}></i>
                                        <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                            {school.name}
                                        </h3>
                                    </div>
                                    <span style={{
                                        padding: '0.2rem 0.6rem',
                                        borderRadius: '20px',
                                        fontSize: '0.75rem',
                                        fontWeight: '700',
                                        background: school.is_active ? '#dcfce7' : '#fee2e2',
                                        color: school.is_active ? '#15803d' : '#b91c1c'
                                    }}>
                                        {school.is_active ? 'Activa' : 'Inactiva'}
                                    </span>
                                </div>

                                <div style={{ fontSize: '0.875rem', color: 'var(--text-color)', display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
                                    {school.contact_person && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <i className="bi bi-person-fill" style={{ color: 'var(--primary-color)', width: '16px' }}></i>
                                            <span><strong>Encargado:</strong> {school.contact_person}</span>
                                        </div>
                                    )}

                                    {school.phone && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <i className="bi bi-telephone-fill" style={{ color: 'var(--primary-color)', width: '16px' }}></i>
                                            <span><strong>Teléfono:</strong> {school.phone}</span>
                                        </div>
                                    )}

                                    {school.address && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <i className="bi bi-geo-alt-fill" style={{ color: 'var(--primary-color)', width: '16px' }}></i>
                                            <span><strong>Dirección:</strong> {school.address}</span>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="bi bi-clock-fill" style={{ color: 'var(--primary-color)', width: '16px' }}></i>
                                        <span><strong>Hora de Entrega:</strong> {school.default_delivery_time || '09:30 AM'}</span>
                                    </div>

                                    {school.notes && (
                                        <div style={{ background: '#FAF6F2', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #E6D7CD', fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                            {school.notes}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Botones de Acción */}
                            <div style={{
                                display: 'flex',
                                gap: '0.5rem',
                                paddingTop: '1rem',
                                borderTop: '1px solid var(--border-color)',
                                alignItems: 'center'
                            }}>
                                <button
                                    onClick={() => handleOpenDispatchModal(school)}
                                    title="Despacho rápido con opción de pago dividido"
                                    style={{
                                        flex: 1,
                                        background: 'var(--primary-color)',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        padding: '0.6rem 0.75rem',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.35rem'
                                    }}
                                >
                                    <i className="bi bi-box-seam"></i> Despachar
                                </button>

                                <button
                                    onClick={() => navigate(`/fast-food/loncheras?schoolId=${school.id}`)}
                                    title="Abrir en Panel de Loncheras Escolares"
                                    style={{
                                        background: '#f8fafc',
                                        color: 'var(--primary-color)',
                                        border: '1px solid var(--border-color)',
                                        padding: '0.6rem 0.75rem',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className="bi bi-arrow-up-right-square"></i>
                                </button>

                                <button
                                    onClick={() => handleOpenEditModal(school)}
                                    title="Editar datos de la escuela"
                                    style={{
                                        background: 'var(--secondary-color)',
                                        color: 'var(--primary-color)',
                                        border: '1px solid var(--border-color)',
                                        padding: '0.6rem 0.85rem',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className="bi bi-pencil-fill"></i>
                                </button>

                                <button
                                    onClick={() => handleToggleActive(school)}
                                    title={school.is_active ? 'Desactivar escuela' : 'Activar escuela'}
                                    style={{
                                        background: school.is_active ? '#fee2e2' : '#dcfce7',
                                        color: school.is_active ? '#dc2626' : '#16a34a',
                                        border: 'none',
                                        padding: '0.6rem 0.85rem',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className={`bi ${school.is_active ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                </button>

                                <button
                                    onClick={() => handleDeleteSchool(school)}
                                    title="Eliminar escuela"
                                    style={{
                                        background: '#f1f5f9',
                                        color: '#64748b',
                                        border: 'none',
                                        padding: '0.6rem 0.85rem',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className="bi bi-trash-fill"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de Despacho Rápido de Loncheras */}
            {showDispatchModal && selectedSchoolForDispatch && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem'
                }}>
                    <div style={{
                        background: '#FFFFFF',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '520px',
                        padding: '1.75rem',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary-color)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <i className="bi bi-box-seam-fill"></i> Despacho Rápido de Loncheras
                            </h2>
                            <button
                                onClick={() => setShowDispatchModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}
                            >
                                &times;
                            </button>
                        </div>

                        <div style={{ backgroundColor: '#FAF6F2', border: '1px solid #E6D7CD', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '14px', color: 'var(--primary-color)' }}>
                                {selectedSchoolForDispatch.name}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                Entrega habitual: {selectedSchoolForDispatch.default_delivery_time || '09:30 AM'} | Tarifa: $1.00 USD c/u
                            </div>
                        </div>

                        <form onSubmit={handleQuickDispatchSubmit}>
                            {/* Fecha de Despacho / Venta */}
                            <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <i className="bi bi-calendar-event" style={{ color: 'var(--primary-color)' }}></i> Fecha del Despacho / Venta
                                    </label>
                                    {dispatchDate !== hoyEcuador() ? (
                                        <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>
                                            Retroactiva
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                                            Hoy
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <input
                                        type="date"
                                        value={dispatchDate}
                                        onChange={(e) => setDispatchDate(e.target.value)}
                                        style={{
                                            flex: 1,
                                            padding: '8px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            color: '#1e293b',
                                            backgroundColor: '#ffffff'
                                        }}
                                    />
                                    {dispatchDate !== hoyEcuador() && (
                                        <button
                                            type="button"
                                            onClick={() => setDispatchDate(hoyEcuador())}
                                            style={{
                                                padding: '0 10px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                background: '#f1f5f9',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                color: '#475569',
                                                cursor: 'pointer'
                                            }}
                                            title="Volver a hoy"
                                        >
                                            Hoy
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Selector de Cliente / Responsable */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                                    Cliente / Responsable (Opcional):
                                </label>
                                {selectedCustomer ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', backgroundColor: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: '8px' }}>
                                        <span style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: '#15803d' }}>
                                            {selectedCustomer.first_name} {selectedCustomer.last_name}
                                            {selectedCustomer.cedula && <span style={{ fontWeight: 'normal', color: '#64748b' }}> · CI: {selectedCustomer.cedula}</span>}
                                        </span>
                                        <button type="button" onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setCustomers([]); }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '16px' }}><i className="bi bi-x-lg"></i></button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <div style={{ position: 'relative', flex: 1 }}>
                                            <input
                                                type="text"
                                                placeholder="Buscar por nombre, cédula o teléfono..."
                                                value={customerSearch}
                                                onChange={(e) => searchCustomers(e.target.value)}
                                                style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                                            />
                                            {customers.length > 0 && (
                                                <div style={{ position: 'absolute', zIndex: 10, width: '100%', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                                                    {customers.map(c => (
                                                        <div key={c.id} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                                                            onClick={() => { setSelectedCustomer(c); setCustomerSearch(`${c.first_name} ${c.last_name}`); setCustomers([]); }}
                                                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                                            <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--primary-color)' }}>{c.first_name} {c.last_name}</div>
                                                            <div style={{ fontSize: '11px', color: '#6b7280' }}>{c.email} {c.cedula && `· CI: ${c.cedula}`}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <button type="button" onClick={() => setShowCustomerModal(true)} title="Crear nuevo cliente"
                                            style={{ width: '38px', height: '38px', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: '300', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            +
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Cantidad de Loncheras */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px' }}>
                                    Cantidad de Loncheras ($1.00 USD) *
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={dispatchQuantity}
                                    onChange={(e) => setDispatchQuantity(e.target.value)}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '8px',
                                        border: '2px solid var(--primary-color)',
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        color: 'var(--primary-color)',
                                        textAlign: 'center'
                                    }}
                                />
                                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                                    {[10, 25, 50, 75, 100].map(q => (
                                        <button
                                            key={q}
                                            type="button"
                                            onClick={() => setDispatchQuantity(q)}
                                            style={{
                                                flex: 1, padding: '4px 0', borderRadius: '6px', border: '1px solid var(--border-color)',
                                                backgroundColor: dispatchQuantity === q ? 'var(--primary-color)' : '#f8fafc',
                                                color: dispatchQuantity === q ? 'white' : 'var(--primary-color)',
                                                fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
                                            }}
                                        >
                                            +{q}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Total Resumen */}
                            <div style={{
                                backgroundColor: 'var(--sidebar-bg)', padding: '12px', borderRadius: '10px',
                                border: '1px dashed var(--border-color)', marginBottom: '16px', textAlign: 'center'
                            }}>
                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: '600' }}>
                                    Total a Cobrar
                                </span>
                                <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--primary-color)' }}>
                                    ${dispatchTotal.toFixed(2)} <span style={{ fontSize: '13px', fontWeight: 'normal' }}>USD</span>
                                </div>
                            </div>

                            {/* Método de Pago */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px' }}>
                                    Forma de Pago
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setDispatchPaymentMethod('efectivo')}
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: dispatchPaymentMethod === 'efectivo' ? 'var(--primary-color)' : 'var(--border-color)',
                                            backgroundColor: dispatchPaymentMethod === 'efectivo' ? 'var(--primary-light)' : 'white',
                                            color: dispatchPaymentMethod === 'efectivo' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                            fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px'
                                        }}
                                    >
                                        <i className="bi bi-cash" style={{ fontSize: '18px' }}></i> Efectivo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDispatchPaymentMethod('transferencia')}
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: dispatchPaymentMethod === 'transferencia' ? 'var(--primary-color)' : 'var(--border-color)',
                                            backgroundColor: dispatchPaymentMethod === 'transferencia' ? 'var(--primary-light)' : 'white',
                                            color: dispatchPaymentMethod === 'transferencia' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                            fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px'
                                        }}
                                    >
                                        <i className="bi bi-bank" style={{ fontSize: '18px' }}></i> Transferencia
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDispatchPaymentMethod('dividido')}
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: dispatchPaymentMethod === 'dividido' ? 'var(--primary-color)' : 'var(--border-color)',
                                            backgroundColor: dispatchPaymentMethod === 'dividido' ? 'var(--primary-light)' : 'white',
                                            color: dispatchPaymentMethod === 'dividido' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                            fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px'
                                        }}
                                    >
                                        <i className="bi bi-pie-chart" style={{ fontSize: '18px' }}></i> Mixto
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDispatchPaymentMethod('fiado')}
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: dispatchPaymentMethod === 'fiado' ? '#dc2626' : 'var(--border-color)',
                                            backgroundColor: dispatchPaymentMethod === 'fiado' ? '#fee2e2' : 'white',
                                            color: dispatchPaymentMethod === 'fiado' ? '#dc2626' : 'var(--text-secondary)',
                                            fontWeight: '700', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px'
                                        }}
                                    >
                                        <i className="bi bi-file-earmark-text" style={{ fontSize: '18px' }}></i> Fiado
                                    </button>
                                </div>

                                {dispatchPaymentMethod === 'fiado' && (
                                    <div style={{
                                        marginTop: '10px',
                                        padding: '10px 12px',
                                        backgroundColor: true ? '#f0fdf4' : '#fef2f2',
                                        borderRadius: '8px',
                                        border: `1px solid ${true ? '#bbf7d0' : '#fecaca'}`,
                                        color: true ? '#166534' : '#991b1b',
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <i className={`bi ${true ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`} style={{ fontSize: '16px' }}></i>
                                        <span>
                                            {selectedCustomer ? (
                                                <>Entrega a <strong>Fiado (Cuenta por Cobrar)</strong> asignada a <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong> ({selectedSchoolForDispatch.name}).</>
                                            ) : (
                                                <>Entrega a <strong>Fiado (Cuenta por Cobrar)</strong> a cargo de <strong>{selectedSchoolForDispatch.name}</strong>. Se cobra desde Trabajadores (vendedor) o Clientes.</>
                                            )}
                                        </span>
                                    </div>
                                )}

                                {dispatchPaymentMethod === 'transferencia' && (
                                    <div style={{ marginTop: '10px' }}>
                                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#374151' }}>
                                            Número de Referencia (Opcional):
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Ej: 12345678"
                                            value={dispatchTransferRef}
                                            onChange={(e) => setDispatchTransferRef(e.target.value)}
                                            style={{
                                                width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)',
                                                borderRadius: '8px', fontSize: '13px', outline: 'none'
                                            }}
                                        />
                                    </div>
                                )}

                                {dispatchPaymentMethod === 'dividido' && (
                                    <div style={{ marginTop: '12px' }}>
                                        <PagoMixto
                                            lineas={mixLines}
                                            onChange={setMixLines}
                                            total={dispatchTotal}
                                            exchangeRate={exchangeRate}
                                            hasDebtor
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Trabajador que vendió */}
                            <SelectorVendedor value={sellerId} onChange={setSellerId} />

                            {/* Notas */}
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '4px' }}>
                                    Notas / Detalle de Entrega (Opcional)
                                </label>
                                <input
                                    type="text"
                                    value={dispatchNotes}
                                    onChange={(e) => setDispatchNotes(e.target.value)}
                                    placeholder="Ej: Entregar en bar escolar"
                                    style={{
                                        width: '100%', padding: '8px 12px', borderRadius: '8px',
                                        border: '1px solid var(--border-color)', fontSize: '13px', outline: 'none'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowDispatchModal(false)}
                                    style={{
                                        padding: '0.85rem 1.25rem', background: '#f1f5f9', color: '#475569',
                                        border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={dispatchSubmitting || !isDispatchPaymentValid}
                                    style={{
                                        flex: 1, padding: '0.85rem 1.5rem',
                                        background: !isDispatchPaymentValid ? '#ef4444' : 'var(--primary-color)',
                                        color: '#FFFFFF', border: 'none', borderRadius: '10px', fontWeight: '700',
                                        cursor: (dispatchSubmitting || !isDispatchPaymentValid) ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 4px 12px rgba(74, 31, 12, 0.2)'
                                    }}
                                >
                                    {dispatchSubmitting ? 'Registrando...'
                                        : !isDispatchPaymentValid ? (mixSummary.valid ? 'Selecciona un cliente para el fiado' : 'Completar Pago Mixto')
                                        : `Registrar Despacho ($${dispatchTotal.toFixed(2)})`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Creación / Edición de Escuela */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem'
                }}>
                    <div style={{
                        background: '#FFFFFF',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '540px',
                        padding: '2rem',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: 'var(--primary-color)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {isEditing ? 'Editar Institución Educativa' : 'Registrar Nueva Escuela'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSaveSchool}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.35rem' }}>
                                    Nombre de la Escuela / Institución *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Ej: Unidad Educativa Tulcán"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color)',
                                        fontSize: '0.95rem',
                                        outline: 'none'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.35rem' }}>
                                        Persona de Contacto / Encargado
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.contact_person}
                                        onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                                        placeholder="Ej: Lcdo. Carlos Benavides"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-color)',
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.35rem' }}>
                                        Teléfono / Celular
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="Ej: 0987654321"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-color)',
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.35rem' }}>
                                        Dirección / Sector en Tulcán
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        placeholder="Ej: Av. Veintimilla y Pichincha"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-color)',
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.35rem' }}>
                                        Hora Entrega Habitual
                                    </label>
                                    <input
                                        type="time"
                                        value={formData.default_delivery_time}
                                        onChange={(e) => setFormData({ ...formData, default_delivery_time: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-color)',
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.35rem' }}>
                                    Notas / Instrucciones de Entrega
                                </label>
                                <textarea
                                    rows="3"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Ej: Entregar en conserjería del bloque A antes de las 09:30."
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color)',
                                        fontSize: '0.95rem',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <input
                                    type="checkbox"
                                    id="is_active_check"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <label htmlFor="is_active_check" style={{ fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer' }}>
                                    Escuela Activa (Disponible para despachos)
                                </label>
                            </div>

                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        padding: '0.85rem 1.25rem',
                                        background: '#f1f5f9',
                                        color: '#475569',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    style={{
                                        flex: 1,
                                        padding: '0.85rem 1.5rem',
                                        background: 'var(--primary-color)',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontWeight: '700',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 4px 12px rgba(74, 31, 12, 0.2)'
                                    }}
                                >
                                    {submitting ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Registrar Escuela')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Nuevo Cliente */}
            {showCustomerModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '1rem' }}>
                    <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '2px solid #e5e7eb', background: 'var(--sidebar-bg)', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="bi bi-person-plus-fill"></i> Nuevo Cliente
                            </h3>
                            <button onClick={() => setShowCustomerModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}>×</button>
                        </div>
                        <form onSubmit={handleCreateCustomer} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Cédula / RUC</label>
                                <input type="text" value={newCustomer.cedula} onChange={e => setNewCustomer(p => ({ ...p, cedula: e.target.value }))} placeholder="Ej: 0401234567"
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Correo Electrónico *</label>
                                <input type="email" required value={newCustomer.email} onChange={e => setNewCustomer(p => ({ ...p, email: e.target.value }))} placeholder="correo@ejemplo.com"
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Nombre *</label>
                                    <input type="text" required value={newCustomer.first_name} onChange={e => setNewCustomer(p => ({ ...p, first_name: e.target.value }))} placeholder="Nombre"
                                        style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Apellido *</label>
                                    <input type="text" required value={newCustomer.last_name} onChange={e => setNewCustomer(p => ({ ...p, last_name: e.target.value }))} placeholder="Apellido"
                                        style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Teléfono</label>
                                <input type="text" value={newCustomer.phone} onChange={e => setNewCustomer(p => ({ ...p, phone: e.target.value }))} placeholder="0987654321"
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #e5e7eb' }}>
                                <button type="button" onClick={() => setShowCustomerModal(false)}
                                    style={{ flex: 1, padding: '0.75rem', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                                    Cancelar
                                </button>
                                <button type="submit"
                                    style={{ flex: 2, padding: '0.75rem', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(74,31,12,0.2)' }}>
                                    <i className="bi bi-person-check-fill"></i> Crear Cliente
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Escuelas;
